import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Badge, Notice, SelectField, TextField, type BadgeProps, type ListColumn } from "@wilco/ui";
import { useCan } from "../auth.ts";
import { clientPaged, DocumentList } from "../docs/DocumentList.tsx";
import { FormPage, useDocumentForm } from "../docs/DocumentForm.tsx";
import { formatDate, formatMoney, formatNumber, t, type MessageKey } from "../i18n.ts";
import { salesApi, type SalesOrder, type SalesState } from "./api.ts";
import { customersQuery, SALES, shipTasksQuery } from "./queries.ts";

const STATE: Record<SalesState, { key: MessageKey; tone: BadgeProps["tone"] }> = {
  DRAFT: { key: "sales.state.DRAFT", tone: "neutral" },
  CONFIRMED: { key: "sales.state.CONFIRMED", tone: "strong" },
  DONE: { key: "sales.state.DONE", tone: "success" },
  CANCELLED: { key: "sales.state.CANCELLED", tone: "neutral" },
};
const STATES = Object.keys(STATE) as SalesState[];

export function SalesStateBadge({ state }: { state: SalesState }) {
  return <Badge tone={STATE[state].tone}>{t(STATE[state].key)}</Badge>;
}

export const orderTotal = (o: SalesOrder) => o.lines.reduce((a, l) => a + l.qty * l.unitPrice, 0);

const route = getRouteApi("/app/sales/orders");

// Сервер отдаёт заказы одним списком: поиск, статус и сортировка в браузере.
const load = clientPaged(salesApi.orders, {
  text: (o) => `${o.number} ${o.customerName}`,
  status: (o) => o.state,
  compare: {
    ship: (a, b) => a.shipDate.localeCompare(b.shipDate),
    shipDesc: (a, b) => b.shipDate.localeCompare(a.shipDate),
    number: (a, b) => b.number.localeCompare(a.number, "uk", { numeric: true }),
  },
});

/**
 * Заказы покупателей на общем списке документов. Кладовщику сверху задачи
 * «Відвантажити покупцю» (ПС-02): отгрузка ждёт, когда изделие упаковано,
 * срок дата заказа.
 */
export function SalesPage() {
  const search = route.useSearch();
  const navigate = useNavigate();
  const can = useCan();
  const tasks = useQuery({ ...shipTasksQuery, enabled: can("sales.ship") });
  const open = (id: string) => void navigate({ to: "/sales/orders/$id", params: { id } });

  const columns: ListColumn<SalesOrder>[] = [
    { key: "number", header: t("sales.number"), kind: "title", width: "9rem", render: (o) => <span className="w-num">{o.number}</span> },
    { key: "customer", header: t("sales.customer"), kind: "text", render: (o) => o.customerName },
    { key: "state", header: t("sales.state"), kind: "text", width: "9rem", render: (o) => <SalesStateBadge state={o.state} /> },
    { key: "ship", header: t("sales.shipDate"), kind: "number", width: "7rem", render: (o) => formatDate(o.shipDate) },
    { key: "left", header: t("sales.remaining"), kind: "number", width: "6rem", render: (o) => formatNumber(o.lines.reduce((a, l) => a + l.remaining, 0)) },
    { key: "sum", header: t("sales.total"), kind: "number", width: "9rem", render: (o) => formatMoney(orderTotal(o), "UAH") },
  ];

  return (
    <DocumentList
      title={t("sales.title")}
      icon="sales"
      queryKey={[...SALES, "orders", "list"]}
      load={load}
      columns={columns}
      rowKey={(o) => o.id}
      onOpen={(o) => open(o.id)}
      statuses={STATES.map((s) => ({ value: s, label: t(STATE[s].key) }))}
      sorts={[
        { value: "ship", label: t("sales.sort.ship") },
        { value: "shipDesc", label: t("sales.sort.shipDesc") },
        { value: "number", label: t("sales.sort.number") },
      ]}
      search={search}
      onSearch={(next) => void navigate({ to: "/sales/orders", search: next, replace: true })}
      searchHint={t("sales.searchHint")}
      create={{ label: t("sales.new"), to: "/sales/orders/new", permission: "sales.order" }}
      empty={{ title: t("sales.empty"), body: t("sales.emptyBody") }}
      before={
        tasks.data?.length ? (
          <Notice tone="info" title={t("sales.tasks", { count: tasks.data.length })} className="stock-notice">
            <ul className="sales-tasks">
              {tasks.data.map((task) => (
                <li key={task.orderId}>
                  <button type="button" className="notice-link w-num" onClick={() => open(task.orderId)}>
                    {task.number}
                  </button>{" "}
                  {t("sales.taskDue", { date: formatDate(task.due) })}
                </li>
              ))}
            </ul>
          </Notice>
        ) : null
      }
    />
  );
}

const inDays = (days: number) => new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10);

interface NewOrder {
  customerId: string;
  shipDate: string;
}

/** Новый заказ покупателя: покупатель из контрагентов и дата отгрузки, строки на карточке. */
export function NewSalesOrderPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const customers = useQuery(customersQuery);
  const form = useDocumentForm<NewOrder, SalesOrder>({
    initial: { customerId: "", shipDate: inDays(7) },
    validate: (v) => ({
      customerId: v.customerId ? undefined : t("docs.required"),
      shipDate: v.shipDate ? undefined : t("docs.required"),
    }),
    submit: (v, key) => salesApi.create(v.customerId, v.shipDate, key),
    onSuccess: (o) => {
      void queryClient.invalidateQueries({ queryKey: SALES });
      void navigate({ to: "/sales/orders/$id", params: { id: o.id }, replace: true });
    },
    // Заказ ссылается на покупателя: отказ про контрагента это его поле.
    fieldOf: (code) => (code === "sales.not_found" ? "customerId" : undefined),
  });
  const { values, set, errors } = form;

  return (
    <FormPage title={t("sales.new")} back="/sales/orders" submitLabel={t("sales.create")} form={form}>
      {customers.data && customers.data.length === 0 ? <Notice tone="warning" title={t("sales.noCustomers")} /> : null}
      <SelectField
        label={t("sales.customer")}
        value={values.customerId}
        onChange={(e) => set("customerId", e.target.value)}
        options={[{ value: "", label: t("docs.choose") }, ...(customers.data ?? []).map((c) => ({ value: c.id, label: c.name }))]}
        error={errors.customerId}
      />
      <TextField label={t("sales.shipDate")} type="date" value={values.shipDate} onChange={(e) => set("shipDate", e.target.value)} error={errors.shipDate} />
      <p className="w-muted">{t("sales.linesAfter")}</p>
    </FormPage>
  );
}
